<script setup lang="ts">
import { ref } from 'vue';
import {
	IonContent,
	IonButton,
	IonFooter,
	IonIcon,
	IonSpinner,
	IonRefresher,
	IonRefresherContent,
	RefresherCustomEvent,
} from '@ionic/vue';
import {
	chevronBack,
	search
} from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

import useQueue from '@/modules/queue/composables/useQueue';

// Init
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Get category ID from route params
const categoryId = parseInt(route.params.categoryId as string);
const categoryName = ref(route.params.categoryName as string || 'Category');

// Queue composable
const { getQueueSubActivities } = useQueue();
const { subActivities, isFetchedSubActivities, refetchSubActivities } = getQueueSubActivities(categoryId);

// Pull to refresh
const handleRefresh = async (event: RefresherCustomEvent) => {
	await refetchSubActivities();
	event.target.complete();
};

// Navigate to activity detail
const goToActivity = (activityId: number) => {
	router.push(`/queue/${activityId}`);
};

// Go back
const goBack = () => {
	router.back();
};
</script>

<template>
	<ion-content class="queue-category-activities-page">
		<ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>

		<!-- Header with back button -->
		<div class="header-section">
			<div class="flex items-center px-6 pt-16 pb-6">
				<ion-button
					fill="clear"
					color="light"
					@click="goBack"
					class="back-button">
					<ion-icon :icon="chevronBack" class="back-icon" />
				</ion-button>
				<h1 class="header-title">{{ categoryName }}</h1>
			</div>
		</div>

		<!-- Content with padding for fixed header -->
		<div class="content-container">
			<!-- Activities List -->
			<div v-if="subActivities && subActivities.length > 0" :class="['activities-list', { 'activities-grid': subActivities.length > 1 }]">
				<div
					v-for="activity in subActivities"
					:key="activity.id"
					class="activity-card"
					@click="goToActivity(activity.id)">
					<div class="card-image-container">
						<img
							:src="activity.image_url || activity.main_image_url || '/api/placeholder/400/240'"
							:alt="activity.name"
							class="card-image" />
						<div class="card-gradient-overlay"></div>
						<div class="card-content">
							<h3 class="card-title">{{ activity.name }}</h3>
							<p class="card-subtitle">{{ activity.short_description || 'Experience the excitement' }}</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Loading state -->
			<div v-else-if="!isFetchedSubActivities" class="loading-state">
				<ion-spinner name="circular"></ion-spinner>
			</div>

			<!-- Empty state -->
			<div v-else class="empty-state">
				<ion-icon :icon="search" class="empty-icon"></ion-icon>
				<h3 class="empty-title">
					{{ t('queue.no_activities_available') }}
				</h3>
				<p class="empty-subtitle">
					{{ t('queue.check_back_later') }}
				</p>
			</div>
		</div>

		<div class="h-[100px]">&nbsp;</div>
	</ion-content>

	<ion-footer>
		<!-- Empty footer or navigation bar if needed -->
	</ion-footer>
</template>

<style lang="scss" scoped>
.queue-category-activities-page {
	--background: #17161e;
}

ion-content {
	--background: #17161e;
}

// Header Section
.header-section {
	background: #17161e;

	.back-button {
		--color: #fff;
		--padding-start: 0;
		--padding-end: 0;
		width: 40px;
		height: 40px;
		margin-right: 16px;
	}

	.back-icon {
		font-size: 24px;
		color: #fff;
	}

	.header-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0;
	}
}

// Content Container
.content-container {
	padding-top: 24px; // Reduced since header is not fixed anymore
	padding-bottom: 100px; // Space for bottom navigation
	min-height: 100vh;
}

// Activities List
.activities-list {
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;

	&.activities-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 16px;
	}
}

// Activity Card
.activity-card {
	width: 100%;
	border-radius: 20px;
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.2s ease;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

	&:active {
		transform: scale(0.98);
	}

	.card-image-container {
		position: relative;
		width: 100%;
		height: 200px;

		.card-image {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.card-gradient-overlay {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 0) 0%,
				rgba(0, 0, 0, 0.2) 40%,
				rgba(0, 0, 0, 0.9) 100%
			);
		}

		.card-content {
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			padding: 20px;
			z-index: 2;

			.card-title {
				color: white;
				font-size: 24px;
				font-weight: 700;
				font-family: 'SourceSansPro-Bold';
				margin-bottom: 4px;
				line-height: 1.2;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}

			.card-subtitle {
				color: rgba(255, 255, 255, 0.9);
				font-size: 14px;
				font-weight: 400;
				font-family: 'SourceSansPro-Regular';
				line-height: 1.4;
				margin: 0;
			}
		}
	}
}

// Loading State
.loading-state {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 400px;

	ion-spinner {
		--color: white;
		width: 48px;
		height: 48px;
	}
}

// Empty State
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	padding: 40px;
	text-align: center;

	.empty-icon {
		font-size: 64px;
		color: rgba(255, 255, 255, 0.3);
		margin-bottom: 20px;
	}

	.empty-title {
		color: white;
		font-size: 20px;
		font-weight: 600;
		font-family: 'SourceSansPro-SemiBold';
		margin-bottom: 8px;
	}

	.empty-subtitle {
		color: rgba(255, 255, 255, 0.6);
		font-size: 16px;
		font-family: 'SourceSansPro-Regular';
		margin: 0;
	}
}
</style>
